import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useNav } from '@/hooks/use-nav';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { usePlatformBranding } from '@/modules/marketing/hooks/use-platform-branding';
import type { NavItem } from '@/types';

interface SidebarProps {
    brand?: string;
    /** Mobile drawer open state — the sidebar is always visible at md+ regardless of this. */
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}

function resolveIcon(name: string) {
    const Icon = (Icons as unknown as Record<string, Icons.LucideIcon>)[name];
    return Icon ?? Icons.Circle;
}

function containsPath(item: NavItem, pathname: string): boolean {
    if (item.href && (pathname === item.href || pathname.startsWith(`${item.href}/`))) {
        return true;
    }
    return (item.children ?? []).some((child) => containsPath(child, pathname));
}

const ACTIVE_CLASSES =
    'bg-blue-50 text-blue-700 font-semibold dark:bg-blue-500/15 dark:text-blue-400 [&_svg]:text-blue-600 dark:[&_svg]:text-blue-400';

function NavNode({ item, depth, onNavigate }: { item: NavItem; depth: number; onNavigate?: () => void }) {
    const location = useLocation();
    const hasChildren = !!item.children?.length;
    const isGroupActive = containsPath(item, location.pathname);
    const [open, setOpen] = useState(() => isGroupActive);
    const Icon = resolveIcon(item.icon);
    const indent = depth === 0 ? '' : depth === 1 ? 'ml-3 pl-3 border-l' : 'ml-6 pl-3 border-l';

    if (!hasChildren) {
        return (
            <NavLink
                to={item.href ?? '#'}
                onClick={onNavigate}
                className={({ isActive }) =>
                    cn(
                        'relative flex items-center gap-2 rounded-md border-l-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                        isActive && cn(ACTIVE_CLASSES, 'border-blue-600'),
                    )
                }
            >
                <Icon className="size-4 shrink-0" />
                <span className="truncate">{item.label}</span>
            </NavLink>
        );
    }

    return (
        <div>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
                    // Depth 0 is a top-level application section (e.g. "Human Resources") and keeps
                    // the brand blue. A nested sub-menu heading (e.g. "Personnel Management" inside
                    // it) gets a visibly different slate tone instead, so a sub-menu is never
                    // mistaken for the section it lives inside.
                    depth === 0 ? 'text-blue-600 dark:text-blue-400' : 'font-semibold text-slate-700 dark:text-slate-300',
                    isGroupActive && !open && ACTIVE_CLASSES,
                )}
            >
                <Icon className="size-4 shrink-0" />
                <span className="flex-1 truncate text-left">{item.label}</span>
                <Icons.ChevronDown className={cn('size-4 shrink-0 transition-transform', !open && '-rotate-90')} />
            </button>

            {open && (
                <div className={cn('mt-1 space-y-1', indent)}>
                    {item.children?.map((child) => (
                        <NavNode key={child.key} item={child} depth={depth + 1} onNavigate={onNavigate} />
                    ))}
                </div>
            )}
        </div>
    );
}

function SidebarBrand({ displayBrand, logoUrl }: { displayBrand: string; logoUrl?: string | null }) {
    return (
        <div className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
            {logoUrl ? (
                <img src={logoUrl} alt={displayBrand} className="h-8 w-auto max-w-[180px] object-contain" />
            ) : (
                <>
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">
                        {displayBrand.charAt(0).toUpperCase()}
                    </div>
                    <span className="truncate text-sm font-semibold">{displayBrand}</span>
                </>
            )}
        </div>
    );
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
    const { data: items, isLoading } = useNav();

    return (
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
            {isLoading && Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}

            {items?.map((item) => (
                <NavNode key={item.key} item={item} depth={0} onNavigate={onNavigate} />
            ))}
        </nav>
    );
}

export function Sidebar({ brand, mobileOpen = false, onMobileClose }: SidebarProps) {
    const { data: branding } = usePlatformBranding();
    const displayBrand = brand ?? branding?.platform_name ?? 'AIPA';

    // Runtime, not build-time — same reasoning as the marketing header's
    // favicon effect (see site-chrome.tsx) — takes effect for logged-in
    // dashboard/admin views the instant a Super Admin uploads one.
    useEffect(() => {
        if (!branding?.favicon_url) return;

        let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = branding.favicon_url;
    }, [branding?.favicon_url]);

    // Locks background scroll while the mobile drawer is open, and lets Escape close it.
    useEffect(() => {
        if (!mobileOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape') onMobileClose?.();
        }
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [mobileOpen, onMobileClose]);

    return (
        <>
            {/* Desktop: always visible, static column. */}
            <aside className="hidden w-64 shrink-0 border-r bg-card md:flex md:flex-col">
                <SidebarBrand displayBrand={displayBrand} logoUrl={branding?.logo_url} />
                <SidebarNav />
            </aside>

            {/* Mobile: an overlay drawer triggered from the topbar's menu button. */}
            <div className={cn('fixed inset-0 z-40 bg-black/50 transition-opacity md:hidden', mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0')} onClick={onMobileClose} aria-hidden="true" />
            <aside
                className={cn(
                    'fixed inset-y-0 left-0 z-50 flex w-72 max-w-[80vw] flex-col border-r bg-card shadow-xl transition-transform duration-200 ease-out md:hidden',
                    mobileOpen ? 'translate-x-0' : '-translate-x-full',
                )}
                role="dialog"
                aria-modal="true"
                aria-hidden={!mobileOpen}
            >
                <SidebarBrand displayBrand={displayBrand} logoUrl={branding?.logo_url} />
                <SidebarNav onNavigate={onMobileClose} />
            </aside>
        </>
    );
}
